import { useEffect, useState, startTransition } from 'react';
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, FileText } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useOrders } from '@/hooks/use-orders';
import { OrderTable } from '@/components/orders/order-table';
import { InvoiceDetailDialog } from '@/components/orders/invoice-detail-dialog';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { CreateOrderDialog } from '@/components/orders/create-order-dialog';
import { CreatePaymentDialog } from '@/components/orders/create-payment-dialog';
import { OrderFilters } from '@/components/orders/order-filters';
import { EditOrderDialog } from '@/components/orders/edit-order-dialog';
import { PageHeader } from '@/components/page-header';
import { ApiOrder } from '@/types';
import { format } from 'date-fns';
import { clampPage, resolveTotalItems, resolveTotalPages } from '@/lib/pagination';

export const Route = createFileRoute('/orders/')({
  validateSearch: (search: Record<string, unknown>): { orderId?: string } => ({
    orderId: search.orderId as string | undefined,
  }),
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <OrdersPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function OrdersPage() {
  const { orderId: searchOrderId } = useSearch({ from: '/orders/' });
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    paymentStatus: 'all',
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined,
  });
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<ApiOrder | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const handleFiltersChange = (nextFilters: typeof filters) => {
    setPage(1);
    setFilters(nextFilters);
  };
  
  const { data: ordersResponse, isLoading, error } = useOrders({
    search: filters.search || undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    paymentStatus: filters.paymentStatus !== 'all' ? filters.paymentStatus : undefined,
    startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
    endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
    page,
  } as any);
  
  const orders = ordersResponse?.data;
  const pagination = ordersResponse?.pagination;
  const totalItems = resolveTotalItems(pagination, orders?.length ?? 0);
  const totalPages = resolveTotalPages(pagination, orders?.length ?? 0);


  return (
    <div className="flex flex-col gap-6 min-w-0 max-w-full">
      <PageHeader
        title="Orders"
        actions={(
          <Link to="/orders/new">
            <Button className="w-full sm:w-auto shadow-lg hover:shadow-xl transition-all font-black uppercase tracking-widest text-[10px] h-11 px-6">
              <Plus className="mr-2 h-4 w-4" />
              New Order
            </Button>
          </Link>
        )}
      />

      {/* Advanced Filters */}
      <OrderFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Orders Table */}
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>All Orders</CardTitle>
          <CardDescription>
            Manage your laundry orders and track payment status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Failed to load orders. Please try again.</p>
            </div>
          ) : !orders?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No orders found</p>
              <p className="text-sm">Try adjusting your filters or create a new order.</p>
            </div>
          ) : (
            <OrderTable 
              orders={orders} 
              onProcessPayment={(order) => {
                setSelectedOrder(order);
                setIsPaymentDialogOpen(true);
              }}
              onEditOrder={(order) => {
                setSelectedOrder(order);
                setIsEditDialogOpen(true);
              }}
              onViewDetails={(id) => {
                navigate({ to: `/orders/${id}` });
              }}
            />
          )}
          
          {pagination && (
             <div className="mt-4">
                <PaginationControls 
                   currentPage={page}
                   totalPages={totalPages}
                   onPageChange={(nextPage: number) => setPage(clampPage(nextPage, totalPages))}
                   totalItems={totalItems}
                   isLoading={isLoading}
                />
             </div>
          )}
        </CardContent>
      </Card>

      <CreateOrderDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen} 
      />
      
      {selectedOrder && (
        <CreatePaymentDialog
          open={isPaymentDialogOpen}
          onOpenChange={setIsPaymentDialogOpen}
          order={selectedOrder}
        />
      )}

      {selectedOrder && (
        <EditOrderDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          order={selectedOrder}
        />
      )}

    </div>
  );
}
